import { createLivestream, createOwnerCheckoutSession } from '../utils/livestreamApi';

/**
 * Livestream Schedule Service
 * Maps to backend ScheduleLivestreamUseCase, LivestreamPresenter, and OwnerCheckoutUseCase
 * Handles the complete flow from creation to owner payment
 * 
 * TEMPORARY CHANGE: Owner payment session creation is commented out for 3-month free scheduling period
 * TODO: Re-enable payment flow after 3 months (around [DATE + 3 months])
 */
class LivestreamScheduleService {
  
  /**
   * Create and schedule a livestream (maps to ScheduleLivestreamUseCase.schedule())
   * @param {Object} livestreamData - Livestream creation data
   * @returns {Promise<Object>} Created livestream with backend validation
   */
  async scheduleLivestream(livestreamData) {
    // Validate required fields before sending to backend
    this.validateScheduleData(livestreamData);
    
    try {
      const response = await createLivestream(livestreamData);
      
      // Backend returns LivestreamDto with court, access, and owner info
      if (response?.data?.success !== undefined) {
        return response.data.data; // ApiEnvelope structure
      } else {
        return response?.data || response; // Legacy structure
      }
    } catch (error) {
      this.handleScheduleError(error);
    }
  }

  /**
   * Validate schedule data according to backend requirements
   * @param {Object} data - Livestream data to validate
   * @throws {Error} If validation fails
   */
  validateScheduleData(data) {
    // Backend ScheduleLivestreamUseCase validation
    if (!data.courtId) {
      throw new Error('Court is required');
    }

    // Required scheduling fields
    if (!data.description) {
      throw new Error('Livestream description is required');
    }

    if (!data.startTime) {
      throw new Error('Start time is required');
    }

    if (!data.endTime) {
      throw new Error('End time is required');
    }

    if (!data.cameraId) {
      throw new Error('Camera selection is required');
    }

    // Validate time logic - handle midnight crossing
    const startTime = new Date(data.startTime);
    const endTime = new Date(data.endTime);
    
    // Check if end time is before start time (crosses midnight)
    let adjustedEndTime = endTime;
    if (endTime < startTime) {
      // End time is on the next day
      adjustedEndTime = new Date(endTime);
      adjustedEndTime.setDate(adjustedEndTime.getDate() + 1);
    }
    
    if (startTime >= adjustedEndTime) {
      throw new Error('End time must be after start time');
    }

    // Validate duration (max 24 hours as per backend)
    const durationHours = (adjustedEndTime - startTime) / (1000 * 60 * 60);
    if (durationHours > 24) {
      throw new Error('Livestream cannot exceed 24 hours');
    }
  }

  /**
   * Handle scheduling errors with backend-specific error mapping
   * @param {Error} error - Error from backend
   * @throws {Error} User-friendly error message
   */
  handleScheduleError(error) {
    console.error('[LivestreamScheduleService] Schedule error:', error);
    
    if (error.response?.status === 400) {
      throw new Error('Invalid livestream data. Please check all fields.');
    } else if (error.response?.status === 403) {
      throw new Error('You do not have permission to schedule livestreams for this court.');
    } else if (error.response?.status === 404) {
      throw new Error('Selected court or camera not found.');
    } else if (error.message?.includes('COURT_NOT_FOUND')) {
      throw new Error('Selected court is not available.');
    } else if (error.message?.includes('COURT_OWNER_NULL')) {
      throw new Error('Court owner information is missing. Please contact support.');
    }
    
    throw error;
  }

  /**
   * Check if owner payment is required (backend logic)
   * @param {Object} livestream - Created livestream data
   * @returns {boolean} True if owner payment is required
   */
  needsOwnerPayment(livestream) {
    // Based on backend OwnerCheckoutUseCase - check requiresPayment flag
    return livestream?.status === 'SCHEDULED' && 
           livestream?.requiresPayment === true && 
           !livestream?.ownerPaid;
  }

  /**
   * Calculate owner payment amount (backend pricing logic)
   * @param {Object} livestream - Livestream data
   * @returns {number} Payment amount in dollars
   */
  calculateOwnerPaymentAmount(livestream) {
    const usageFee = livestream?.usageFee || 0;
    const hourlyPrice = livestream?.hourlyPrice || 0;
    const viewersPrice = livestream?.viewersPrice || 0;

    if (usageFee > 0) return usageFee;

    if (hourlyPrice > 0 && livestream?.startTime && livestream?.endTime) {
      const start = new Date(livestream.startTime);
      let end = new Date(livestream.endTime);

      // Honor crossesMidnight like the server
      if (livestream.crossesMidnight && end <= start) {
        end = new Date(end);
        end.setDate(end.getDate() + 1);
      }

      const hours = Math.max(0, (end - start) / 36e5);
      return hourlyPrice * hours;
    }

    return viewersPrice > 0 ? viewersPrice : 0;
  }

  /**
   * Process owner payment flow
   * @param {Object} livestream - Livestream requiring payment
   * @param {Object} user - Current user
   * @param {string} discountCode - Optional discount code
   * @returns {Promise<string|null>} Checkout URL or null if no payment needed
   */
  async processOwnerPayment(livestream, user, discountCode = null) {
    if (!this.needsOwnerPayment(livestream)) {
      return null; // No payment required
    }

    try {
      const paymentAmount = this.calculateOwnerPaymentAmount(livestream);
      
      if (paymentAmount <= 0) {
        throw new Error('Cannot determine payment amount');
      }

      const paymentRequest = {
        userId: user.id,
        discountCode: discountCode?.trim() || null,
        successUrl: `${window.location.origin}/owner/livestreams/${livestream.id}/checkout/success`,
        cancelUrl: `${window.location.origin}/livestream/manage`
      };

      const checkoutResponse = await createOwnerCheckoutSession(livestream.id, paymentRequest);
      
      if (checkoutResponse?.data?.success !== undefined) {
        return checkoutResponse.data.data.url; // ApiEnvelope structure
      } else {
        return checkoutResponse?.url || null; // Legacy structure
      }
    } catch (error) {
      console.error('[LivestreamScheduleService] Owner payment error:', error);
      throw new Error('Failed to create owner payment session');
    }
  }

  /**
   * Complete livestream schedule flow with owner payment handling
   * @param {Object} formData - Form data from scheduling component
   * @param {Object} user - Current user
   * @returns {Promise<Object>} Completion result
   */
  async completeScheduleFlow(formData, user) {
    console.log('🔍 completeScheduleFlow - Current user ID:', user?.id || 'No user provided');
    
    try {
      // 1. Schedule the livestream
      const scheduledLivestream = await this.scheduleLivestream(formData);
      
      const result = {
        success: true,
        livestream: scheduledLivestream,
        paymentRequired: false,
        paymentUrl: null,
        message: 'Livestream scheduled successfully!'
      };

      // 2. Check if owner payment is needed
      // TEMPORARY: Commented out for 3-month free scheduling period
      // TODO: Re-enable after 3 months (around [DATE + 3 months])
      /*
      if (this.needsOwnerPayment(scheduledLivestream)) {
        const checkoutUrl = await this.processOwnerPayment(
          scheduledLivestream, 
          user, 
          formData.discountCode
        );
        
        if (checkoutUrl) {
          result.paymentRequired = true;
          result.paymentUrl = checkoutUrl;
          result.message = 'Livestream scheduled! Owner payment required to enable streaming.';
        }
      }
      */

      return result;
    } catch (error) {
      console.error('[LivestreamScheduleService] Complete flow error:', error);
      return {
        success: false,
        error: error.message || 'Failed to schedule livestream',
        livestream: null
      };
    }
  }

  /**
   * Format livestream data for backend submission
   * @param {Object} formState - Frontend form state
   * @param {Array} courts - Available courts
   * @returns {Object} Backend-compatible livestream data
   */
  formatScheduleData(formState, courts) {
    // Convert local time to ISO format for backend
    const toLocal = (dateStr, timeStr) => `${dateStr}T${timeStr}:00`;
    
    const startLocal = toLocal(formState.date, formState.startTime);
    const endLocal = toLocal(formState.date, formState.endTime);
    
    console.log('🔍 formatScheduleData - Current user (from formState):', formState.user?.id || 'No user in formState');
    
    // Use browser clock only to decide if UI intended "overnight"
    const start = new Date(startLocal);
    const end = new Date(endLocal);
    const crossesMidnight = end <= start; // same-day UI entry that logically ends next day
    
    // Get timezone info
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const utcOffsetMinutes = -new Date().getTimezoneOffset();
    
    return {
      // Required fields
      description: formState.livestreamName || formState.description || 'Livestream Event',
      startTime: startLocal, // LocalDateTime style, no TZ
      endTime: endLocal, // LocalDateTime style, no TZ
      
      // Court association
      courtId: Number(formState.selectedCourtId),
      
      // Camera assignment
      cameraId: Number(formState.selectedCameraId),
      cameraName: formState.selectedCamera,
      
      // Pricing structure (backend expects these fields)
      price: formState.viewersPrice || 0,
      viewersPrice: formState.viewersPrice || 0,
      hourlyPrice: formState.hourlyPrice || 0,
      usageFee: formState.usageFee || 0,
      
      // Privacy settings
      isPublic: formState.isPublic !== undefined ? formState.isPublic : true,
      freeLivestream: formState.isPublic !== undefined ? formState.isPublic : true,
      
      // Payment requirements (private streams require payment)
      requiresPayment: formState.isPublic === false,
      
      // Game information
      gameType: formState.gameType || null,
      teamAName: formState.teamAName || null,
      teamBName: formState.teamBName || null,
      doubles: formState.doubles || false,
      
      // Sponsors and branding
      sponsors: formState.sponsors || [],
      logoType: formState.logoType || 'AKTURE',
      
      // Recording settings
      recordingEnabled: formState.recordingEnabled || false,
      
      // Status (will be overridden by backend to SCHEDULED)
      status: 'SCHEDULED',
      
      // Additional metadata
      ownerPaid: false, // Will be updated after payment
      discountCode: formState.discountCode || null,

      // Timezone metadata for backend to handle midnight crossing and local time interpretation
      clientTimeZone: tz,
      utcOffsetMinutes,
      crossesMidnight
    };
  }

  /**
   * Get livestream status after creation
   * @param {Object} livestream - Created livestream
   * @returns {Object} Status information
   */
  getScheduleStatus(livestream) {
    const status = {
      scheduled: true,
      needsOwnerPayment: this.needsOwnerPayment(livestream),
      ownerPaid: livestream?.ownerPaid || false,
      paymentAmount: this.calculateOwnerPaymentAmount(livestream),
      canStart: livestream?.status === 'SCHEDULED' && livestream?.ownerPaid,
      accessInfo: livestream?.access || {}
    };

    return status;
  }
}

// Export singleton instance
export default new LivestreamScheduleService();
